/**
 * GraphNoeudsPage — Doctrine 51 (V23.16.0).
 *
 * Page d'exploration multi-niveaux du graphe de dépendances. Le nœud est
 * l'unité de base. Cliquer sur un voisin descend d'un niveau ; un breadcrumb
 * permet de remonter à tout moment.
 *
 * Panneau latéral : les 6 questions structurelles répondues par NOAM
 * (cache 7j, bouton "Régénérer" pour forcer).
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Compass, Loader2, Sparkles, Network, RefreshCw, ChevronRight, Home,
  AlertTriangle, Layers, Download, Plus, Waves, X, Check, Trash2, TrendingDown, Crown,
} from "lucide-react";
import DependencyGraphV2 from "@/components/DependencyGraphV2";
import { SplcButton } from "@/components/AnnuaireSPLC";
import ValueChainRiver from "@/components/ValueChainRiver";
import MostCriticalDependencyBanner from "@/components/MostCriticalDependencyBanner";

export default function GraphNoeudsPage() {
  const [allNodes, setAllNodes] = useState(null); // full list (for picker)
  const [centerId, setCenterId] = useState(null);
  const [expand, setExpand] = useState(null); // { center_id, nodes, edges }
  const [breadcrumb, setBreadcrumb] = useState([]); // [{id, name}]
  const [selectedNode, setSelectedNode] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false); // V23.16.11 — Auto-gen NOAM
  // V23.16.1 — depth control + dialogs
  const [depth, setDepth] = useState(1);
  const [showEdgeModal, setShowEdgeModal] = useState(false);
  const [showRiverModal, setShowRiverModal] = useState(false);
  const [cascade, setCascade] = useState(null);
  const [cascadeBusy, setCascadeBusy] = useState(false);

  const loadAllNodes = useCallback(async () => {
    try {
      const r = await api.get("/workspace/graph/nodes?limit=500");
      setAllNodes(r.data.nodes || []);
      // V23.16.11 — Préférence stricte : on n'auto-sélectionne QUE le is_self.
      // Si aucun is_self, on laisse centerId null → la page affiche un onboarding
      // (bouton "Générer mon graphe automatiquement via NOAM") au lieu de
      // tomber sur un nœud aléatoire de l'Annuaire (ex: "Smart Quotient").
      if (!centerId && r.data.nodes?.length) {
        const self = r.data.nodes.find((n) => n.is_self);
        if (self) {
          setCenterId(self.id);
          setBreadcrumb([{ id: self.id, name: self.name }]);
        }
      }
    } catch (e) {
      notify.error(fmtError(e, "Chargement impossible."));
      setAllNodes([]);
    } finally {
      setLoading(false);
    }
  }, [centerId]);

  useEffect(() => { loadAllNodes(); }, [loadAllNodes]);

  // V23.16.11 — Auto-génération du graphe via NOAM depuis le positionnement
  const autoGenerate = async () => {
    setAutoGenerating(true);
    try {
      const r = await api.post("/workspace/graph/auto-generate");
      if (r.data.already_generated) {
        notify.info("Un graphe existe déjà — rechargement.");
      } else {
        notify.success(r.data.message || `Graphe généré.`);
      }
      // Reload + auto-center sur le nouveau is_self
      const list = await api.get("/workspace/graph/nodes?limit=500");
      setAllNodes(list.data.nodes || []);
      const self = (list.data.nodes || []).find((n) => n.is_self);
      if (self) {
        setCenterId(self.id);
        setBreadcrumb([{ id: self.id, name: self.name }]);
      }
    } catch (e) {
      notify.error(fmtError(e, "Génération impossible."));
    } finally {
      setAutoGenerating(false);
    }
  };

  const loadExpand = useCallback(async (nodeId) => {
    if (!nodeId) return;
    try {
      const r = await api.get(`/workspace/graph/nodes/${nodeId}/expand?depth=${depth}`);
      setExpand(r.data);
      const center = (r.data.nodes || []).find((n) => n.id === nodeId);
      setSelectedNode(center || null);
    } catch (e) {
      notify.error(fmtError(e, "Expansion impossible."));
    }
  }, [depth]);

  useEffect(() => { if (centerId) loadExpand(centerId); }, [centerId, loadExpand]);

  const goToNode = (node) => {
    if (!node || node.id === centerId) {
      setSelectedNode(node);
      return;
    }
    setCenterId(node.id);
    setBreadcrumb((b) => {
      const existing = b.findIndex((x) => x.id === node.id);
      if (existing >= 0) return b.slice(0, existing + 1);
      return [...b, { id: node.id, name: node.name }];
    });
    setAnalysis(null);
  };

  const jumpToBreadcrumb = (idx) => {
    const target = breadcrumb[idx];
    if (!target) return;
    setCenterId(target.id);
    setBreadcrumb((b) => b.slice(0, idx + 1));
    setAnalysis(null);
  };

  const analyzeCurrent = async (force = false) => {
    if (!centerId) return;
    setAnalyzing(true);
    try {
      const r = await api.post(`/workspace/graph/nodes/${centerId}/analyze${force ? "?force=true" : ""}`);
      setAnalysis(r.data);
    } catch (e) {
      notify.error(fmtError(e, "Analyse impossible."));
    } finally {
      setAnalyzing(false);
    }
  };

  const importAnnuaire = async () => {
    setImporting(true);
    try {
      const r = await api.post("/workspace/graph/import-from-annuaire");
      notify.success(`${r.data.imported} importés · ${r.data.skipped} déjà présents.`);
      await loadAllNodes();
    } catch (e) {
      notify.error(fmtError(e, "Import impossible."));
    } finally {
      setImporting(false);
    }
  };

  // V23.16.1 — Import value-chain (rivière) → graphe unifié + arêtes auto
  const importValueChain = async () => {
    setImporting(true);
    try {
      const r = await api.post("/workspace/graph/import-from-value-chain");
      const e = r.data?.edges_created || 0;
      notify.success(`${r.data.imported} nœud(s) importés · ${e} arête(s) créées.`);
      await loadAllNodes();
      if (centerId) await loadExpand(centerId);
    } catch (e) {
      notify.error(fmtError(e, "Import rivière impossible."));
    } finally {
      setImporting(false);
    }
  };

  // V23.16.4 — Importer "Mon organisation" comme nœud central du graphe
  const importMyOrg = async () => {
    setImporting(true);
    try {
      // Récupère le profil de l'org active + le positionnement
      const me = await api.get("/workspace/me");
      const pos = await api.get("/workspace/positionnement").catch(() => ({ data: null }));
      const org = me.data?.organisation || {};
      const profile = pos.data?.profile || {};
      const name = org.name || "Mon organisation";
      // Vérifier si déjà présent (par nom case-insensitive)
      const existing = (allNodes || []).find(
        (n) => (n.name || "").toLowerCase() === name.toLowerCase()
          && (n.type === "organisation" || n.is_self)
      );
      if (existing) {
        notify.info("« Mon organisation » est déjà présente dans le graphe.");
        setCenterId(existing.id);
        return;
      }
      const r = await api.post("/workspace/graph/nodes", {
        name,
        type: "organisation",
        role: profile.sector_free || profile.sector || org.sector || "",
        description: profile.description || org.description || "",
        is_self: true,
        criticality: 90,
        substitutability: 10,
        source: "manual",
      });
      const newNode = r.data;
      notify.success(`« ${name} » ajoutée au graphe.`);
      await loadAllNodes();
      if (newNode?.id) setCenterId(newNode.id);
    } catch (e) {
      notify.error(fmtError(e, "Impossible d'importer l'organisation."));
    } finally {
      setImporting(false);
    }
  };

  // V23.16.4 — Suppression d'un nœud (avec arêtes liées) + cascade UI
  const deleteNode = async (node) => {
    if (!node) return;
    const ok = window.confirm(
      `Supprimer définitivement le nœud « ${node.name} » ?\n\n` +
      `Toutes les arêtes connectées seront aussi supprimées. Action irréversible.`
    );
    if (!ok) return;
    try {
      await api.delete(`/workspace/graph/nodes/${node.id}`);
      notify.success(`« ${node.name} » supprimé.`);
      // Si on supprime le centre actuel, on remonte d'un niveau
      if (centerId === node.id) {
        if (breadcrumb.length > 1) {
          setCenterId(breadcrumb[breadcrumb.length - 2].id);
        } else {
          setCenterId(null);
        }
      }
      setSelectedNode(null);
      await loadAllNodes();
      if (centerId && centerId !== node.id) await loadExpand(centerId);
    } catch (e) {
      notify.error(fmtError(e, "Suppression impossible."));
    }
  };

  // V23.16.1 — Cascade What-if : NOAM simule la disparition du nœud sélectionné
  const simulateCascade = async () => {
    if (!selectedNode) return;
    setCascadeBusy(true);
    try {
      const r = await api.post(`/workspace/graph/nodes/${selectedNode.id}/cascade`);
      setCascade(r.data);
    } catch (e) {
      notify.error(fmtError(e, "Cascade impossible."));
    } finally {
      setCascadeBusy(false);
    }
  };

  // V23.16.1 — Création d'arête depuis le picker
  const createEdge = async ({ to_node_id, kind, weight }) => {
    if (!selectedNode) return;
    try {
      await api.post("/workspace/graph/edges", {
        from_node_id: selectedNode.id,
        to_node_id, kind, weight,
      });
      notify.success("Arête créée.");
      setShowEdgeModal(false);
      if (centerId) await loadExpand(centerId);
    } catch (e) {
      notify.error(fmtError(e, "Création arête impossible."));
    }
  };

  // V23.16.1 — Construit un objet rivière à partir du chemin from→self→to du graphe
  // pour la projection ValueChainRiver. Heuristique : neighbors de _depth=1 répartis
  // sur le flow_position selon le type (regulateur=amont, fournisseur=amont, partenaire=proche, client=aval).
  const buildRiverNodes = useCallback(() => {
    if (!expand) return [];
    const nodes = expand.nodes || [];
    const selfN = nodes.find((n) => n.is_self) || nodes.find((n) => n.id === expand.center_id);
    const position_by_type = {
      regulateur: 8, fournisseur: 22, tech: 22,
      partenaire: 42, organisation: 50,
      client: 78, substitut: 60, other: 35,
    };
    return nodes.map((n) => ({
      id: n.id,
      name: n.name,
      role: n.type,
      is_self: n.id === selfN?.id,
      flow_position: n.id === selfN?.id ? 50 : (position_by_type[n.type] || 40),
      capture_pct: n.criticality || 30,
      negotiation_weight_pct: n.market_concentration || 30,
      branch: (n._depth || 0) > 1 ? 1 : 0,
    }));
  }, [expand]);

  if (loading) {
    return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500 mx-auto" /></div>;
  }

  // V23.16.11 — État vide OU pas de is_self : on propose l'auto-génération NOAM
  const hasSelf = (allNodes || []).some((n) => n.is_self);
  if (allNodes && (allNodes.length === 0 || !hasSelf)) {
    return (
      <div className="space-y-4" data-testid="graph-page">
        <PageHeader />
        <div className="noam-card p-10 text-center" data-testid="graph-empty-state">
          <Network className="w-10 h-10 text-[#7F77DD] mx-auto opacity-70" />
          <h4 className="noam-display text-lg mt-4 text-zinc-100">
            {allNodes.length === 0
              ? "Construisez votre graphe de dépendances"
              : "Aucun nœud auto-référent — générons votre graphe"}
          </h4>
          <p className="text-zinc-400 text-sm mt-2 max-w-lg mx-auto">
            NOAM analyse votre <strong className="text-zinc-200">positionnement</strong> et votre{" "}
            <strong className="text-zinc-200">annuaire</strong> pour identifier 8 à 12 acteurs réels
            de votre écosystème (régulateurs, fournisseurs, clients, partenaires, concurrents) et
            tracer automatiquement les relations typées. Aucune saisie manuelle requise.
          </p>
          <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
            <button
              onClick={autoGenerate}
              disabled={autoGenerating || importing}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="graph-auto-generate"
            >
              {autoGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {autoGenerating ? "Génération en cours (10-30s)…" : "Générer mon graphe via NOAM"}
            </button>
            <button
              onClick={importAnnuaire}
              disabled={importing || autoGenerating}
              className="text-xs px-3 py-2 rounded border border-white/10 text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="graph-import-annuaire"
            >
              {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              Ou importer depuis l&apos;Annuaire (sans IA)
            </button>
          </div>
          <p className="text-[10px] text-zinc-600 mt-4">
            La génération utilise votre contexte réel (secteur, zone, description) — les acteurs
            cités sont des entités existantes, pas inventées.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="graph-page">
      <PageHeader>
        <div className="inline-flex items-center gap-1.5 mr-2" data-testid="graph-depth-picker">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Profondeur</span>
          {[1, 2, 3].map((d) => (
            <button
              key={d}
              onClick={() => setDepth(d)}
              className={`w-7 h-7 rounded text-xs font-medium transition ${depth === d ? "bg-[#7F77DD] text-white" : "bg-white/[0.04] text-zinc-400 hover:bg-white/[0.08]"}`}
              data-testid={`graph-depth-${d}`}
            >
              {d}
            </button>
          ))}
        </div>
        <button
          onClick={importMyOrg}
          disabled={importing}
          className="btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="graph-import-my-org-btn"
          title="Créer un nœud central « Mon organisation » à partir du profil actuel"
        >
          <Crown className="w-3.5 h-3.5" /> Importer Mon Org
        </button>
        <button
          onClick={importValueChain}
          disabled={importing}
          className="btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="graph-import-river-btn"
          title="Importer les acteurs de la rivière Origination (Chaîne de valeur) + créer les arêtes typées automatiquement"
        >
          <Waves className="w-3.5 h-3.5" /> Importer Rivière
        </button>
        <button
          onClick={importAnnuaire}
          disabled={importing}
          className="btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="graph-import-annuaire-btn"
          title="Réimporter les nouvelles entités Annuaire (idempotent)"
        >
          {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Importer Annuaire
        </button>
      </PageHeader>

      {/* V23.15.32 — Doctrine 3Q · "Et alors ?" en tête de vue */}
      <MostCriticalDependencyBanner />

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 flex-wrap text-xs" data-testid="graph-breadcrumb">
        <button onClick={() => jumpToBreadcrumb(0)} className="inline-flex items-center gap-1 text-zinc-400 hover:text-zinc-100">
          <Home className="w-3 h-3" /> Racine
        </button>
        {breadcrumb.slice(1).map((b, i) => (
          <span key={b.id} className="inline-flex items-center gap-1">
            <ChevronRight className="w-3 h-3 text-zinc-700" />
            <button
              onClick={() => jumpToBreadcrumb(i + 1)}
              className={`hover:text-zinc-100 ${i === breadcrumb.length - 2 ? "text-[#EF9F27] font-medium" : "text-zinc-400"}`}
              data-testid={`graph-breadcrumb-${b.id}`}
            >
              {b.name}
            </button>
          </span>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        {/* Graphe */}
        <div className="noam-card p-4">
          {expand ? (
            <DependencyGraphV2
              centerId={expand.center_id}
              nodes={expand.nodes}
              edges={expand.edges}
              selectedId={selectedNode?.id || centerId}
              onSelectNode={goToNode}
              height={460}
              testIdPrefix="graph-viz"
            />
          ) : (
            <div className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-500 mx-auto" /></div>
          )}
          <div className="text-[10px] text-zinc-500 mt-2 text-center">
            Cliquez sur un voisin pour descendre · Breadcrumb pour remonter
          </div>
        </div>

        {/* Panneau analyse */}
        <div className="noam-card p-4 min-h-[460px]" data-testid="graph-analysis-panel">
          {selectedNode ? (
            <>
              {/* V23.16.1 — Action bar */}
              <div className="flex items-center gap-1.5 pb-3 mb-3 border-b border-white/5 flex-wrap">
                <button
                  onClick={() => setShowEdgeModal(true)}
                  className="text-[10px] px-2 py-1 rounded bg-[#7F77DD]/15 border border-[#7F77DD]/40 text-[#7F77DD] hover:bg-[#7F77DD]/25 inline-flex items-center gap-1"
                  data-testid="graph-add-edge-btn"
                  title="Créer une arête typée depuis ce nœud"
                >
                  <Plus className="w-3 h-3" /> Arête
                </button>
                <button
                  onClick={() => setShowRiverModal(true)}
                  className="text-[10px] px-2 py-1 rounded bg-[#EF9F27]/15 border border-[#EF9F27]/40 text-[#EF9F27] hover:bg-[#EF9F27]/25 inline-flex items-center gap-1"
                  data-testid="graph-river-projection-btn"
                  title="Projection rivière du voisinage actuel"
                >
                  <Waves className="w-3 h-3" /> Vue rivière
                </button>
                <SplcButton
                  entityName={selectedNode.label || selectedNode.name}
                  testid="graph-node-splc-btn"
                />
                <button
                  onClick={simulateCascade}
                  disabled={cascadeBusy}
                  className="text-[10px] px-2 py-1 rounded bg-[#E24B4A]/15 border border-[#E24B4A]/40 text-[#E24B4A] hover:bg-[#E24B4A]/25 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="graph-cascade-btn"
                  title="Simuler la disparition de ce nœud et estimer l'impact en cascade"
                >
                  {cascadeBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingDown className="w-3 h-3" />}
                  Cascade
                </button>
                <button
                  onClick={() => deleteNode(selectedNode)}
                  className="ml-auto text-[10px] px-2 py-1 rounded bg-white/[0.04] border border-white/10 text-zinc-400 hover:text-[#E24B4A] hover:border-[#E24B4A]/40 inline-flex items-center gap-1"
                  data-testid="graph-delete-node-btn"
                  title="Supprimer ce nœud et toutes ses arêtes (irréversible)"
                >
                  <Trash2 className="w-3 h-3" /> Supprimer
                </button>
              </div>
              {cascade ? (
                <CascadePreview
                  cascade={cascade}
                  nodes={expand?.nodes || []}
                  onDiscard={() => setCascade(null)}
                />
              ) : (
                <NodeAnalysisPanel
                  node={selectedNode}
                  analysis={analysis}
                  analyzing={analyzing}
                  onAnalyze={analyzeCurrent}
                />
              )}
            </>
          ) : (
            <div className="text-center text-zinc-500 text-sm py-12">
              Sélectionnez un nœud pour voir ses propriétés.
            </div>
          )}
        </div>
      </div>

      {/* V23.16.1 — Edge picker modal */}
      {showEdgeModal && selectedNode && (
        <EdgePickerModal
          fromNode={selectedNode}
          candidates={(allNodes || []).filter((n) => n.id !== selectedNode.id)}
          onClose={() => setShowEdgeModal(false)}
          onSubmit={createEdge}
        />
      )}

      {/* V23.16.1 — Projection rivière (réutilise ValueChainRiver) */}
      {showRiverModal && (
        <RiverProjectionModal
          riverNodes={buildRiverNodes()}
          onClose={() => setShowRiverModal(false)}
        />
      )}
    </div>
  );
}

function PageHeader({ children }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-4">
      <div>
        <div className="flex items-center gap-2 text-[#7F77DD] noam-overline">
          <Compass className="w-3 h-3" /> Stratégique · Cartographie multi-niveaux
        </div>
        <h1 className="noam-display text-3xl font-bold tracking-tight mt-1 inline-flex items-center gap-2">
          <Network className="w-6 h-6 text-[#EF9F27]" />
          Graphe de dépendances
        </h1>
        <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
          Le nœud est l&apos;unité de base. Explorez niveau par niveau, posez les 6 questions structurelles.
        </p>
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

function NodeAnalysisPanel({ node, analysis, analyzing, onAnalyze }) {
  const hasAnalysis = !!(analysis && analysis.bottleneck);
  return (
    <div className="space-y-3" data-testid={`graph-node-analysis-${node.id}`}>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">
          {node.is_self ? "Votre organisation" : (node.type || "Nœud").toUpperCase()}
        </div>
        <h3 className="text-base font-semibold text-zinc-100 mt-0.5 break-words">
          {node.is_self && <span className="text-[#EF9F27] mr-1">★</span>}
          {node.name}
        </h3>
        {node.role && <div className="text-xs text-zinc-400 mt-0.5">{node.role}</div>}
      </div>

      {/* Métriques unifiées */}
      <div className="grid grid-cols-2 gap-2 pt-3 border-t border-white/5">
        <MetricCard label="Criticité" value={node.criticality} color="#E24B4A" />
        <MetricCard label="Substituabilité" value={node.substitutability} color="#1D9E75" />
        <MetricCard label="Concentration marché" value={node.market_concentration} color="#7F77DD" />
        <MetricCard label="Pouvoir multiplication" value={node.multiplier_power} color="#EF9F27" />
      </div>

      {/* 6 questions structurelles */}
      <div className="pt-3 border-t border-white/5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-[#EF9F27]" /> Analyse structurelle (Doctrine 51)
          </span>
          <button
            onClick={() => onAnalyze(hasAnalysis)}
            disabled={analyzing}
            className="text-[10px] inline-flex items-center gap-1 text-[#7F77DD] hover:text-[#9F95FF] disabled:opacity-50"
            data-testid={`graph-analyze-btn-${node.id}`}
          >
            {analyzing ? <Loader2 className="w-3 h-3 animate-spin" /> :
              hasAnalysis ? <RefreshCw className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
            {analyzing ? "Analyse…" : hasAnalysis ? "Régénérer" : "Analyser"}
          </button>
        </div>

        {!hasAnalysis && !analyzing && (
          <p className="text-xs text-zinc-500 italic">
            Cliquez sur « Analyser » pour que NOAM réponde aux 6 questions structurelles.
          </p>
        )}

        {hasAnalysis && (
          <div className="space-y-3">
            <Question label="Goulot d'étranglement principal">
              <p className="text-xs text-zinc-200 leading-relaxed">{analysis.bottleneck}</p>
            </Question>
            <Question label="5 plus grandes dépendances">
              <ListItems items={analysis.top_dependencies} />
            </Question>
            <Question label="Besoins fondamentaux satisfaits">
              <ListItems items={analysis.needs_satisfied} />
            </Question>
            <Question label="Moteurs économiques dominants">
              <ListItems items={analysis.economic_drivers} />
            </Question>
            <Question label="Industries qui profitent">
              <ListItems items={analysis.industries_benefiting} />
            </Question>
            <Question label="Industries affectées s'il disparaissait">
              <ListItems items={analysis.industries_affected_if_disappears} color="#E24B4A" />
            </Question>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }) {
  const v = Number(value) || 0;
  return (
    <div className="bg-white/[0.03] rounded p-2">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="flex items-center gap-2 mt-1">
        <div className="text-sm font-medium text-zinc-100">{v}<span className="text-[10px] text-zinc-500">/100</span></div>
        <div className="flex-1 h-1 bg-white/[0.06] rounded-full overflow-hidden">
          <div className="h-full" style={{ width: `${v}%`, background: color }} />
        </div>
      </div>
    </div>
  );
}

function Question({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function ListItems({ items, color = "#A1A1AA" }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-[11px] text-zinc-600 italic">—</p>;
  }
  return (
    <ul className="space-y-0.5">
      {items.map((it, i) => (
        <li key={i} className="text-xs text-zinc-300 leading-snug flex items-start gap-1.5">
          <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: color }} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}


// --------------------------------------------------------------------
// V23.16.1 — Sous-composants (modales + cascade)
// --------------------------------------------------------------------

const SEVERITY_COLOR = { low: "#1D9E75", medium: "#EF9F27", high: "#E24B4A" };

function CascadePreview({ cascade, nodes, onDiscard }) {
  const sev = (cascade?.severity || "medium").toLowerCase();
  const sevColor = SEVERITY_COLOR[sev] || "#A1A1AA";
  const nodeName = (id) => (nodes.find((n) => n.id === id)?.name || id);
  return (
    <div className="space-y-3" data-testid="graph-cascade-preview">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5">
          <TrendingDown className="w-3.5 h-3.5" style={{ color: sevColor }} />
          <span className="text-[10px] uppercase tracking-wider" style={{ color: sevColor }}>
            Cascade · Sévérité {sev}
          </span>
        </div>
        <button
          onClick={onDiscard}
          className="text-[10px] text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1"
          data-testid="graph-cascade-discard"
        >
          <X className="w-3 h-3" /> Fermer
        </button>
      </div>
      <h3 className="text-sm font-semibold text-zinc-100">
        Disparition simulée de « {cascade.node_name} »
      </h3>
      {cascade.justification && (
        <p className="text-xs text-zinc-300 leading-relaxed bg-white/[0.03] rounded p-2 border border-white/5">
          {cascade.justification}
        </p>
      )}
      {Array.isArray(cascade.affected_nodes) && cascade.affected_nodes.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3 text-[#E24B4A]" /> Nœuds affectés
          </div>
          <ul className="space-y-1">
            {cascade.affected_nodes.map((a) => {
              const imp = (a.impact || "medium").toLowerCase();
              const impactColor = SEVERITY_COLOR[imp] || "#A1A1AA";
              return (
                <li
                  key={a.node_id}
                  className="text-xs flex items-start gap-1.5"
                  data-testid={`graph-cascade-node-${a.node_id}`}
                >
                  <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: impactColor }} />
                  <div>
                    <span className="text-zinc-100">{nodeName(a.node_id)}</span>
                    <span className="ml-1 text-[10px] uppercase" style={{ color: impactColor }}>· {imp}</span>
                    {a.reason && <div className="text-zinc-400 text-[11px] leading-snug">{a.reason}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {Array.isArray(cascade.affected_industries) && cascade.affected_industries.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 inline-flex items-center gap-1.5">
            <Layers className="w-3 h-3 text-[#7F77DD]" /> Industries impactées
          </div>
          <div className="flex flex-wrap gap-1">
            {cascade.affected_industries.map((ind, i) => (
              <span
                key={i}
                className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.05] border border-white/10 text-zinc-300"
              >
                {ind}
              </span>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(cascade.mitigations) && cascade.mitigations.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 inline-flex items-center gap-1.5">
            <Check className="w-3 h-3 text-[#1D9E75]" /> Mitigations recommandées
          </div>
          <ul className="space-y-0.5">
            {cascade.mitigations.map((m, i) => (
              <li key={i} className="text-xs text-zinc-300 leading-snug flex items-start gap-1.5">
                <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0 bg-[#1D9E75]" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const EDGE_KINDS = [
  { id: "depends_on", label: "Dépend de" },
  { id: "supplies", label: "Fournit" },
  { id: "regulates", label: "Régule" },
  { id: "partner", label: "Partenaire" },
  { id: "competes", label: "Concurrence" },
  { id: "owns", label: "Détient" },
  { id: "audits", label: "Audite" },
  { id: "finances", label: "Finance" },
];

function EdgePickerModal({ fromNode, candidates, onClose, onSubmit }) {
  const [toId, setToId] = useState("");
  const [kind, setKind] = useState("depends_on");
  const [weight, setWeight] = useState(50);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const q = search.trim().toLowerCase();
  const filtered = (candidates || [])
    .filter((c) => !q || (c.name || "").toLowerCase().includes(q))
    .slice(0, 80);

  const submit = async () => {
    if (!toId) return;
    setBusy(true);
    try {
      await onSubmit({ to_node_id: toId, kind, weight: Number(weight) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="graph-edge-modal"
    >
      <div
        className="noam-card p-5 w-full max-w-md space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-100 inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5 text-[#7F77DD]" /> Nouvelle arête
          </h3>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-200"
            data-testid="graph-edge-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="text-xs text-zinc-400">
          Depuis <span className="text-[#EF9F27] font-medium">{fromNode.name}</span>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Type de relation</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="w-full mt-1 bg-white/[0.04] border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-100"
            data-testid="graph-edge-kind"
          >
            {EDGE_KINDS.map((k) => (
              <option key={k.id} value={k.id}>{k.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Vers</label>
          <input
            type="text"
            placeholder="Rechercher un nœud…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full mt-1 bg-white/[0.04] border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600"
            data-testid="graph-edge-search"
          />
          <div className="max-h-44 overflow-y-auto mt-1 border border-white/5 rounded">
            {filtered.length === 0 ? (
              <div className="text-[11px] text-zinc-600 italic p-2">Aucun nœud.</div>
            ) : filtered.map((c) => (
              <button
                key={c.id}
                onClick={() => setToId(c.id)}
                className={`w-full text-left px-2 py-1.5 text-xs flex items-center justify-between hover:bg-white/[0.05] ${toId === c.id ? "bg-[#7F77DD]/15 text-[#7F77DD]" : "text-zinc-300"}`}
                data-testid={`graph-edge-target-${c.id}`}
              >
                <span className="truncate">{c.name}</span>
                <span className="text-[10px] text-zinc-500 ml-2">{c.type}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">
            Poids · {weight}/100
          </label>
          <input
            type="range"
            min={0}
            max={100}
            value={weight}
            onChange={(e) => setWeight(Number(e.target.value))}
            className="w-full mt-1 accent-[#7F77DD]"
            data-testid="graph-edge-weight"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded border border-white/10 text-zinc-400 hover:text-zinc-100"
            data-testid="graph-edge-cancel"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={!toId || busy}
            className="text-xs px-3 py-1.5 rounded bg-[#7F77DD] text-white disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="graph-edge-submit"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Créer
          </button>
        </div>
      </div>
    </div>
  );
}

function RiverProjectionModal({ riverNodes, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="graph-river-modal"
    >
      <div
        className="noam-card p-5 w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-zinc-100 inline-flex items-center gap-1.5">
            <Waves className="w-3.5 h-3.5 text-[#EF9F27]" /> Projection rivière du voisinage
          </h3>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-200"
            data-testid="graph-river-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {(!riverNodes || riverNodes.length === 0) ? (
          <div className="text-center text-zinc-500 text-sm py-12">
            Aucun voisin à projeter. Importez la rivière ou créez des arêtes.
          </div>
        ) : (
          <ValueChainRiver
            nodes={riverNodes}
            height={420}
            testIdPrefix="graph-river-proj"
          />
        )}
        <div className="text-[10px] text-zinc-500 mt-2 text-center">
          Projection narrative · amont (gauche) → aval (droite). Vue lecture seule.
        </div>
      </div>
    </div>
  );
}
